import { Injectable, Pipe, PipeTransform } from "@angular/core";
import { HeaderGroupComponent } from "../../views/header-group-component/header-group.component";
import { DatePipe } from "@angular/common";
import { ExcelStyle } from "ag-grid-community";

@Injectable({
  providedIn: "root",
})
@Pipe({
  name: "groupAndSort",
})
export class GroupedHeaderervice implements PipeTransform {
  groupedHeader;
  columnDefs;

  private tryParseDateTime(str: string): number | null {
    if (!str) return null;
    const months: { [key: string]: number } = {
      January: 0,
      Jan: 0,
      February: 1,
      Feb: 1,
      March: 2,
      Mar: 2,
      April: 3,
      Apr: 3,
      May: 4,
      June: 5,
      Jun: 5,
      July: 6,
      Jul: 6,
      August: 7,
      Aug: 7,
      September: 8,
      Sep: 8,
      Sept: 8,
      October: 9,
      Oct: 9,
      November: 10,
      Nov: 10,
      December: 11,
      Dec: 11,
    };

    // Pattern 1: "DD-MonthName-YYYY [time]" — e.g., "03-April-2026 14:30:00"
    const named = str.match(
      /^(\d{1,2})[\/\-]([A-Za-z]+)[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    );
    if (named) {
      const [, day, mon, year, h = "0", m = "0", s = "0", ampm] = named;
      const cap = mon.charAt(0).toUpperCase() + mon.slice(1).toLowerCase();
      if (months[cap] !== undefined) {
        let hr = parseInt(h, 10);
        if (ampm?.toUpperCase() === "PM" && hr < 12) hr += 12;
        if (ampm?.toUpperCase() === "AM" && hr === 12) hr = 0;
        return new Date(+year, months[cap], +day, hr, +m, +s).getTime();
      }
    }

    // Pattern 2: "MonthName-DD-YYYY [time]" — e.g., "April-03-2026 14:30:00"
    const namedFirst = str.match(
      /^([A-Za-z]+)[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    );
    if (namedFirst) {
      const [, mon, day, year, h = "0", m = "0", s = "0", ampm] = namedFirst;
      const cap = mon.charAt(0).toUpperCase() + mon.slice(1).toLowerCase();
      if (months[cap] !== undefined) {
        let hr = parseInt(h, 10);
        if (ampm?.toUpperCase() === "PM" && hr < 12) hr += 12;
        if (ampm?.toUpperCase() === "AM" && hr === 12) hr = 0;
        return new Date(+year, months[cap], +day, hr, +m, +s).getTime();
      }
    }

    // Pattern 3: "YYYY-MonthName-DD [time]" — e.g., "2026-April-03 14:30:00"
    const yearNamed = str.match(
      /^(\d{4})[\/\-]([A-Za-z]+)[\/\-](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    );
    if (yearNamed) {
      const [, year, mon, day, h = "0", m = "0", s = "0", ampm] = yearNamed;
      const cap = mon.charAt(0).toUpperCase() + mon.slice(1).toLowerCase();
      if (months[cap] !== undefined) {
        let hr = parseInt(h, 10);
        if (ampm?.toUpperCase() === "PM" && hr < 12) hr += 12;
        if (ampm?.toUpperCase() === "AM" && hr === 12) hr = 0;
        return new Date(+year, months[cap], +day, hr, +m, +s).getTime();
      }
    }

    // Pattern 4: "YYYY-MM-DD [time]" — year-first numeric (e.g., "2026-05-13 14:30:00")
    const iso = str.match(
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    );
    if (iso) {
      const [, year, month, day, h = "0", m = "0", s = "0", ampm] = iso;
      let hr = parseInt(h, 10);
      if (ampm?.toUpperCase() === "PM" && hr < 12) hr += 12;
      if (ampm?.toUpperCase() === "AM" && hr === 12) hr = 0;
      return new Date(+year, +month - 1, +day, hr, +m, +s).getTime();
    }

    // Pattern 5: "NN-NN-YYYY [time]" — numeric, day-or-month first
    const numeric = str.match(
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i,
    );
    if (numeric) {
      const [, part1, part2, year, h = "0", m = "0", s = "0", ampm] = numeric;
      let hr = parseInt(h, 10);
      if (ampm?.toUpperCase() === "PM" && hr < 12) hr += 12;
      if (ampm?.toUpperCase() === "AM" && hr === 12) hr = 0;
      const p1 = parseInt(part1, 10);
      const p2 = parseInt(part2, 10);
      let month: number, day: number;
      if (p1 > 12) {
        // part1 can only be a day → DD-MM-YYYY
        day = p1;
        month = p2;
      } else if (p2 > 12) {
        // part2 can only be a day → MM-DD-YYYY
        month = p1;
        day = p2;
      } else {
        // ambiguous — resolve from user settings stored in localStorage
        try {
          const stored = localStorage.getItem("userSettings");
          const fmt = stored ? JSON.parse(stored).userDateFormat || "" : "";
          if (fmt.startsWith("MM") || fmt.startsWith("mm")) {
            // MM-dd-yyyy: first part is month
            month = p1;
            day = p2;
          } else {
            // dd-MM-yyyy (or unknown): first part is day
            day = p1;
            month = p2;
          }
        } catch {
          day = p1;
          month = p2;
        }
      }
      return new Date(+year, month - 1, day, hr, +m, +s).getTime();
    }

    return null;
  }

  private readonly smartDateComparator = (
    valueA: string,
    valueB: string,
  ): number => {
    const tsA = this.tryParseDateTime(valueA);
    const tsB = this.tryParseDateTime(valueB);
    if (tsA !== null && tsB !== null) return tsA - tsB;
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return -1;
    if (valueB == null) return 1;
    return String(valueA).localeCompare(String(valueB));
  };

  constructor(private datePipe: DatePipe) {
    //call user defined DATE TIME FORMAT
  }
  transform(data: any) {
    const groupedData = data.reduce((acc, item) => {
      const parentID = item.parentID;

      if (!acc[parentID]) {
        acc[parentID] = [];
      }
      acc[parentID].push(item);

      return acc;
    }, {});

    this.groupedHeader = Object.keys(groupedData).map((key) => {
      if (key !== "0") {
        return {
          parentID: key,
          arrayID: groupedData[key][0].arrayID,
          sortingID: groupedData[key][0].sortingID,
          parentName: groupedData[key][0].parentName,
          filter: groupedData[key][0].filter,
          hide: groupedData[key][0].hide,
          ChartDataType: groupedData[key][0].ChartDataType,
          children: groupedData[key],
        };
      } else {
        return groupedData[key];
      }
    });
    if (this.groupedHeader[0][0].parentID == 0) {
      for (let i = 0; i < this.groupedHeader[0].length; i++) {
        this.groupedHeader.push(this.groupedHeader[0][i]);
      }
      this.groupedHeader.splice(0, 1);
    }
    this.groupedHeader.forEach((group) => {
      if (group.children) {
        group.children.sort((a, b) => a.sortingID - b.sortingID);
      }
    });
    this.groupedHeader.sort((a: any, b: any) => a.sortingID - b.sortingID);
    let y: any = [];
    this.columnDefs = [];
    for (let i = 0; i < this.groupedHeader.length; i++) {
      y = [];
      if (this.groupedHeader[i].children != null) {
        for (let j = 0; j <= this.groupedHeader[i].children.length - 1; j++) {
          y.push({
            headerName: this.groupedHeader[i].children[j].headerTooltip,
            headerTooltip: this.groupedHeader[i].children[j].headerTooltip,
            colId: this.groupedHeader[i].children[j].arrayID,
            field: this.groupedHeader[i].children[j].headerName,
            tooltipField: this.groupedHeader[i].children[j].headerTooltip,
            hide: this.groupedHeader[i].children[j].hide,
            minWidth: 130,
            width: Number(this.groupedHeader[i].children[j].width),
            suppressResize: false,
            columnGroupShow: this.groupedHeader[i].children[j].columnGroupShow,
            filter: this.groupedHeader[i].children[j].filter,
            suppressColumnsToolPanel:
              this.groupedHeader[i].children[j].suppressColumnsToolPanel ===
              "true"
                ? true
                : false,
            enablePivot: true,
            chartDataType: this.groupedHeader[i].children[j].chartDataType,
            pinned: this.groupedHeader[i].children[j].pinned,
            comparator:
              this.groupedHeader[i].children[j].filter === "agTextColumnFilter"
                ? this.smartDateComparator
                : undefined,
            customParam: this.groupedHeader[i].children[j].url,
            valueFormatter: (params) => {
              const columnFilter = this.groupedHeader[i].children[j].filter;
              if (
                columnFilter === "agNumberColumnFilter" &&
                typeof params.value === "number"
              ) {
                return params.value.toFixed(2);
              }
              return params.value || "";
            },
          });
        }
        this.columnDefs.push({
          headerName: this.groupedHeader[i].parentName,
          headerTooltip: this.groupedHeader[i].headerTooltip,
          colId: this.groupedHeader[i].arrayID,
          hide: this.groupedHeader[i].hide,
          filter: this.groupedHeader[i].filter,
          chartDataType: this.groupedHeader[i].chartDataType,
          customParam: this.groupedHeader[i].url,
          suppressColumnsToolPanel:
            this.groupedHeader[i].suppressColumnsToolPanel === "true"
              ? true
              : false,
          headerGroupComponentFramework: HeaderGroupComponent,
          children: y,
          flex: 1,
          minWidth: 190,
          pinned: true,
        });
      } else {
        let width;
        let minwidth;

        if (this.groupedHeader[i].headerName == "Site ID") {
          width = 95;
          minwidth = 95;
        } else if (
          this.groupedHeader[i].headerName == "sitename" ||
          this.groupedHeader[i].headerName == "SiteName" ||
          this.groupedHeader[i].headerName == "alarms"
        ) {
          width = 240;
          minwidth = 130;
        } else if (this.groupedHeader[i].headerName == "LastUpdated") {
          width = 175;
          minwidth = 130;
        } else {
          width = Number(this.groupedHeader[i].width);
          minwidth = 100;
        }

        this.columnDefs.push({
          headerName: this.groupedHeader[i].headerTooltip,
          headerTooltip: this.groupedHeader[i].headerTooltip,
          colId: this.groupedHeader[i].arrayID,
          field: this.groupedHeader[i].headerName,
          hide: this.groupedHeader[i].hide,
          tooltipField: this.groupedHeader[i].headerTooltip,
          cellRenderer: this.groupedHeader[i].cellrenderer,
          filter: this.groupedHeader[i].filter,
          width: width,
          minWidth: minwidth,
          flex: 1,
          customParam: this.groupedHeader[i].url,
          chartDataType: this.groupedHeader[i].chartDataType,
          pinned: this.groupedHeader[i].pinned,
          comparator:
            this.groupedHeader[i].filter === "agTextColumnFilter"
              ? this.smartDateComparator
              : undefined,
          suppressColumnsToolPanel:
            this.groupedHeader[i].suppressColumnsToolPanel === "true"
              ? true
              : false,

          valueFormatter: (params) => {
            const columnFilter = this.groupedHeader[i].filter;
            if (
              columnFilter === "agNumberColumnFilter" &&
              typeof params.value === "number"
            ) {
              return params.value.toFixed(2);
            }
            return params.value || "";
          },
        });
      }
    }
    return this.columnDefs;
  }
}
